import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listAppointments } from "@/api/audit";
import { AuditReportView } from "@/components/AuditReportView";
import { EzyVetMeta } from "@/components/EzyVetMeta";
import { useTenant } from "@/hooks/useTenant";
import type { ApiError, AppointmentInfo } from "@/types";

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : "—";
}

function sourceLabel(a: AppointmentInfo): string {
  return a.external_source === "ezyvet" ? `ezyVet consult #${a.external_id}` : "Manual upload";
}

/** Every audited appointment for the clinic: manual uploads and, for ezyVet clinics, synced consults. */
export function AppointmentsPage() {
  const navigate = useNavigate();
  const { isEzyVet } = useTenant();
  const appointments = useQuery<AppointmentInfo[], ApiError>({
    queryKey: ["appointments"],
    queryFn: () => listAppointments(),
  });

  return (
    <div>
      <div className="page-header">
        <h1>Appointments</h1>
        <p className="page-sub">
          {isEzyVet
            ? "Consults from ezyVet, audited as soon as they change, plus any audits you ran by hand."
            : "Every appointment you've audited."}
        </p>
      </div>

      {appointments.isLoading && (
        <div className="card loading">
          <div className="spinner" />
          <div>Loading appointments…</div>
        </div>
      )}
      {appointments.error && (
        <div className="card form-error">Failed to load appointments: {appointments.error.message}</div>
      )}
      {appointments.data?.length === 0 && (
        <div className="card muted">
          No appointments yet. <Link to="/audit">Run an audit</Link>
          {isEzyVet && " or wait for the next ezyVet sync"}.
        </div>
      )}

      {appointments.data && appointments.data.length > 0 && (
        <div className="card table-card">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Source</th>
                <th>Status</th>
                <th className="num">Missing charges</th>
                <th className="num">Leakage</th>
              </tr>
            </thead>
            <tbody>
              {appointments.data.map((a) => (
                <tr key={a.id} className="clickable" onClick={() => navigate(`/appointments/${a.id}`)}>
                  <td>{formatDate(a.appointment_date ?? a.created_at)}</td>
                  <td>
                    <Link to={`/appointments/${a.id}`} onClick={(e) => e.stopPropagation()}>
                      {a.patient_name || "Unnamed patient"}
                    </Link>
                  </td>
                  <td className="muted">{sourceLabel(a)}</td>
                  <td>{a.audit_status}</td>
                  <td className="num">{a.missing_charges}</td>
                  <td className="num">${a.estimated_leakage_usd.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** One appointment's audit, with its ezyVet ids and links when it was synced. */
export function AppointmentDetailPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { ezyvetSite } = useTenant();
  if (!appointmentId) return null;

  return (
    <div>
      <div className="page-header">
        <Link to="/appointments" className="muted">
          ← Appointments
        </Link>
      </div>
      <AuditReportView
        appointmentId={appointmentId}
        header={(report) =>
          report.appointment && (
            <div className="card">
              <h1 style={{ marginBottom: 4 }}>{report.appointment.patient_name || "Unnamed patient"}</h1>
              <p className="page-sub">
                {formatDate(report.appointment.appointment_date ?? report.appointment.created_at)}
                {report.appointment.vet_name && ` · ${report.appointment.vet_name}`}
                {` · ${sourceLabel(report.appointment)}`}
              </p>
              <EzyVetMeta appointment={report.appointment} site={ezyvetSite} />
            </div>
          )
        }
      />
    </div>
  );
}
